import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, Form, Button, Table, Modal,
  Spinner, Alert, ListGroup,
} from "react-bootstrap";
import { FaDumbbell, FaPlus, FaTrash, FaCalendarAlt, FaEdit, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSearchParams } from "react-router-dom";
import { api } from "../../contexts/AuthContext";

const SIDEBAR_BLUE = "#60A5FA";

const WEEKDAY_LABELS = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

const MUSCLE_GROUPS = [
  { value: "chest", label: "Chest" },
  { value: "back", label: "Back" },
  { value: "shoulders", label: "Shoulders" },
  { value: "arms", label: "Arms" },
  { value: "legs", label: "Legs" },
  { value: "core", label: "Core" },
  { value: "full_body", label: "Full body" },
  { value: "cardio", label: "Cardio" },
  { value: "other", label: "Other" },
];

const WARMUP_PRESETS = [
  { name: "Walk 5'", duration_minutes: 5 },
  { name: "Walk 10'", duration_minutes: 10 },
  { name: "Light bike 5'", duration_minutes: 5 },
  { name: "Jumping jacks", duration_minutes: 2 },
  { name: "Arm circles", duration_minutes: 2 },
];

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDate = (value) => {
  if (!value) return new Date();
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const displayDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return `${d}/${m}/${y}`;
};

const inputStyle = {
  backgroundColor: "#2d2d2d",
  border: `1px solid ${SIDEBAR_BLUE}`,
  color: "#fff",
};

const cardStyle = {
  backgroundColor: "#1e1e1e",
  border: `1px solid ${SIDEBAR_BLUE}`,
};

const muscleLabel = (value) =>
  MUSCLE_GROUPS.find((g) => g.value === value)?.label || value;

const WorkoutPlanner = () => {
  const [searchParams] = useSearchParams();
  const focusDate = searchParams.get("date");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDay, setSelectedDay] = useState(new Date());

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [exerciseSection, setExerciseSection] = useState("main");
  const [warmupMode, setWarmupMode] = useState("duration"); // duration | sets
  const [applyWeekdays, setApplyWeekdays] = useState(() => new Set([new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]));
  const [savingExercise, setSavingExercise] = useState(false);
  const [planForm, setPlanForm] = useState({
    name: "",
    start_date: new Date(),
    end_date: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000),
    notes: "",
  });

  const [exerciseForm, setExerciseForm] = useState({
    name: "",
    muscle_group: "chest",
    target_sets: 3,
    target_reps: 8,
    target_weight_kg: "",
    rest_seconds: 90,
    duration_minutes: 5,
    notes: "",
  });

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bootstrap = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/api/workout-plans/");
      const planList = res.data.results || res.data;
      setPlans(planList);

      if (focusDate) {
        const match = planList.find(
          (plan) => plan.start_date <= focusDate && plan.end_date >= focusDate
        );
        if (match) {
          await openPlan(match.id);
          setSelectedDay(parseDate(focusDate));
        }
      }
    } catch (err) {
      console.error(err);
      setError("Could not load workout plans.");
    } finally {
      setLoading(false);
    }
  };

  const openPlan = async (planId) => {
    const detail = await api.get(`/api/workout-plans/${planId}/`);
    setSelectedPlan(detail.data);
    const start = parseDate(detail.data.start_date);
    const end = parseDate(detail.data.end_date);
    setSelectedDay((current) => {
      if (current >= start && current <= end) return current;
      return start;
    });
  };

  const jsToPlanWeekday = (date) => {
    const jsDay = date.getDay(); // Sun=0
    return jsDay === 0 ? 6 : jsDay - 1;
  };

  const selectedDayStr = formatDate(selectedDay);

  const datesForWeekdays = (weekdaySet) => {
    if (!selectedPlan) return [];
    const start = parseDate(selectedPlan.start_date);
    const end = parseDate(selectedPlan.end_date);
    const dates = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      if (weekdaySet.has(jsToPlanWeekday(cursor))) {
        dates.push(formatDate(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  };

  const applyDates = datesForWeekdays(applyWeekdays);

  const toggleApplyWeekday = (weekday) => {
    setApplyWeekdays((prev) => {
      const next = new Set(prev);
      if (next.has(weekday)) {
        if (next.size > 1) next.delete(weekday);
      } else {
        next.add(weekday);
      }
      return next;
    });
  };

  const selectAllWeekdays = () => {
    setApplyWeekdays(new Set([0, 1, 2, 3, 4, 5, 6]));
  };

  const selectWeekdaysOnly = () => {
    setApplyWeekdays(new Set([0, 1, 2, 3, 4]));
  };

  const selectThisDayOnly = () => {
    setApplyWeekdays(new Set([jsToPlanWeekday(selectedDay)]));
  };

  const resetPlanForm = () => {
    setEditingPlanId(null);
    setPlanForm({
      name: "",
      start_date: new Date(),
      end_date: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000),
      notes: "",
    });
  };

  const openCreatePlanModal = () => {
    resetPlanForm();
    setShowPlanModal(true);
  };

  const openEditPlanModal = (plan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name || "",
      start_date: parseDate(plan.start_date),
      end_date: parseDate(plan.end_date),
      notes: plan.notes || "",
    });
    setShowPlanModal(true);
  };

  const blockedDateIntervals = plans
    .filter((plan) => plan.id !== editingPlanId)
    .map((plan) => ({
      start: parseDate(plan.start_date),
      end: parseDate(plan.end_date),
    }));

  const rangesOverlap = (startA, endA, startB, endB) => startA <= endB && endA >= startB;

  const findPlanOverlap = (start, end) => {
    const startStr = formatDate(start);
    const endStr = formatDate(end);
    return plans.find(
      (plan) =>
        plan.id !== editingPlanId &&
        rangesOverlap(startStr, endStr, plan.start_date, plan.end_date)
    );
  };

  const setRangePreset = (days) => {
    const start = planForm.start_date ? new Date(planForm.start_date) : new Date();
    const end = new Date(start);
    end.setDate(start.getDate() + days - 1);
    setPlanForm((prev) => ({ ...prev, start_date: start, end_date: end }));
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    if (planForm.end_date < planForm.start_date) {
      setError("End date must be on or after start date.");
      return;
    }
    const conflict = findPlanOverlap(planForm.start_date, planForm.end_date);
    if (conflict) {
      setError(
        `Date range overlaps with "${conflict.name}" (${displayDate(conflict.start_date)} → ${displayDate(conflict.end_date)}).`
      );
      return;
    }
    const payload = {
      name: planForm.name.trim(),
      start_date: formatDate(planForm.start_date),
      end_date: formatDate(planForm.end_date),
      notes: planForm.notes,
    };
    try {
      setError(null);
      if (editingPlanId) {
        await api.patch(`/api/workout-plans/${editingPlanId}/`, payload);
        await openPlan(editingPlanId);
        const listRes = await api.get("/api/workout-plans/");
        setPlans(listRes.data.results || listRes.data);
      } else {
        const res = await api.post("/api/workout-plans/", payload);
        setPlans((prev) => [res.data, ...prev]);
        setSelectedPlan(res.data);
        setSelectedDay(parseDate(res.data.start_date));
      }
      setShowPlanModal(false);
      resetPlanForm();
    } catch (err) {
      console.error(err);
      const apiMsg =
        err.response?.data?.start_date?.[0] ||
        err.response?.data?.end_date?.[0];
      setError(apiMsg || "Could not save workout plan.");
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await api.delete(`/api/workout-plans/${planId}/`);
      setPlans((prev) => prev.filter((plan) => plan.id !== planId));
      if (selectedPlan?.id === planId) setSelectedPlan(null);
    } catch (err) {
      console.error(err);
      setError("Could not delete plan.");
    }
  };

  const activeDayWorkout = (selectedPlan?.sessions || []).find(
    (s) => s.date === selectedDayStr
  ) || null;

  const clampSelectedDay = (date) => {
    if (!selectedPlan) return date;
    const start = parseDate(selectedPlan.start_date);
    const end = parseDate(selectedPlan.end_date);
    if (date < start) return start;
    if (date > end) return end;
    return date;
  };

  const shiftSelectedDay = (days) => {
    setSelectedDay((current) => {
      const next = new Date(current);
      next.setDate(next.getDate() + days);
      return clampSelectedDay(next);
    });
  };

  const handleClearDay = async () => {
    if (!activeDayWorkout || !selectedPlan) return;
    try {
      await api.delete(`/api/workout-sessions/${activeDayWorkout.id}/`);
      await openPlan(selectedPlan.id);
    } catch (err) {
      console.error(err);
      setError("Could not clear this day's workout.");
    }
  };

  const openExerciseModal = (section) => {
    setExerciseSection(section);
    setWarmupMode(section === "warmup" ? "duration" : "sets");
    setExerciseForm({
      name: "",
      muscle_group: section === "warmup" ? "cardio" : "chest",
      target_sets: section === "warmup" ? 1 : 3,
      target_reps: section === "warmup" ? 10 : 8,
      target_weight_kg: "",
      rest_seconds: section === "warmup" ? 30 : 90,
      duration_minutes: 5,
      notes: "",
    });
    setShowExerciseModal(true);
  };

  const addExerciseToDates = async (payload) => {
    if (!selectedPlan) return;
    if (applyDates.length === 0) {
      setError("Select at least one weekday to apply this exercise.");
      return;
    }
    setSavingExercise(true);
    try {
      await api.post("/api/workout-exercises/bulk-create/", {
        plan: selectedPlan.id,
        dates: applyDates,
        ...payload,
      });
      await openPlan(selectedPlan.id);
    } finally {
      setSavingExercise(false);
    }
  };

  const handleAddWarmupPreset = async (preset) => {
    if (!selectedPlan) return;
    try {
      setError(null);
      await addExerciseToDates({
        section: "warmup",
        muscle_group: "cardio",
        name: preset.name,
        target_sets: 0,
        target_reps: 0,
        target_weight_kg: null,
        rest_seconds: 0,
        duration_minutes: preset.duration_minutes,
        notes: "",
      });
    } catch (err) {
      console.error(err);
      setError("Could not add warm-up.");
    }
  };

  const handleAddExercise = async (e) => {
    e.preventDefault();
    if (!selectedPlan || !exerciseForm.name.trim()) return;
    const isDuration = exerciseSection === "warmup" && warmupMode === "duration";
    try {
      setError(null);
      await addExerciseToDates({
        section: exerciseSection,
        muscle_group: exerciseForm.muscle_group,
        name: exerciseForm.name.trim(),
        target_sets: isDuration ? 0 : Number(exerciseForm.target_sets) || 1,
        target_reps: isDuration ? 0 : Number(exerciseForm.target_reps) || 1,
        target_weight_kg:
          isDuration || exerciseForm.target_weight_kg === "" || exerciseForm.target_weight_kg == null
            ? null
            : Number(exerciseForm.target_weight_kg),
        rest_seconds: isDuration ? 0 : Number(exerciseForm.rest_seconds) || 0,
        duration_minutes: isDuration ? Number(exerciseForm.duration_minutes) || 5 : null,
        notes: exerciseForm.notes,
      });
      setShowExerciseModal(false);
    } catch (err) {
      console.error(err);
      setError("Could not add exercise.");
    }
  };

  const handleCopyDayToSelected = async () => {
    if (!selectedPlan || !activeDayWorkout) return;
    const exercises = activeDayWorkout.exercises || [];
    if (exercises.length === 0) {
      setError("This day has no exercises to copy.");
      return;
    }
    const targetDates = applyDates.filter((d) => d !== selectedDayStr);
    if (targetDates.length === 0) {
      setError("Select other weekdays to copy this day onto.");
      return;
    }
    try {
      setError(null);
      setSavingExercise(true);
      for (const exercise of exercises) {
        await api.post("/api/workout-exercises/bulk-create/", {
          plan: selectedPlan.id,
          dates: targetDates,
          section: exercise.section,
          muscle_group: exercise.muscle_group,
          name: exercise.name,
          target_sets: exercise.target_sets,
          target_reps: exercise.target_reps,
          target_weight_kg: exercise.target_weight_kg,
          rest_seconds: exercise.rest_seconds,
          duration_minutes: exercise.duration_minutes,
          notes: exercise.notes || "",
        });
      }
      await openPlan(selectedPlan.id);
    } catch (err) {
      console.error(err);
      setError("Could not copy day.");
    } finally {
      setSavingExercise(false);
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    try {
      await api.delete(`/api/workout-exercises/${exerciseId}/`);
      await openPlan(selectedPlan.id);
    } catch (err) {
      console.error(err);
      setError("Could not delete exercise.");
    }
  };

  const exercisesBySection = (section) =>
    (activeDayWorkout?.exercises || [])
      .filter((ex) => ex.section === section)
      .sort((a, b) => a.order - b.order || a.id - b.id);

  const groupByMuscle = (exercises) => {
    const groups = {};
    exercises.forEach((ex) => {
      const key = ex.muscle_group || "other";
      if (!groups[key]) groups[key] = [];
      groups[key].push(ex);
    });
    return groups;
  };

  const scheduledDays = (selectedPlan?.sessions || [])
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));

  const renderExerciseTable = (exercises, isWarmup = false) => {
    if (exercises.length === 0) {
      return <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>Nothing added yet.</p>;
    }
    if (isWarmup) {
      return (
        <Table responsive hover variant="dark" size="sm" style={{ color: "#fff" }}>
          <thead>
            <tr>
              <th>Warm-up</th>
              <th>Details</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {exercises.map((ex) => (
              <tr key={ex.id}>
                <td>{ex.name}</td>
                <td>
                  {ex.duration_minutes
                    ? `${ex.duration_minutes} min`
                    : `${ex.target_sets}×${ex.target_reps}${ex.target_weight_kg != null ? ` @ ${ex.target_weight_kg}kg` : ""}`}
                </td>
                <td>
                  <Button
                    variant="link"
                    size="sm"
                    style={{ color: "#F87171" }}
                    onClick={() => handleDeleteExercise(ex.id)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      );
    }

    const grouped = groupByMuscle(exercises);
    return Object.entries(grouped).map(([group, items]) => (
      <div key={group} className="mb-3">
        <div className="mb-2" style={{ color: SIDEBAR_BLUE, fontWeight: 600, fontSize: "0.85rem" }}>
          {muscleLabel(group)}
        </div>
        <Table responsive hover variant="dark" size="sm" style={{ color: "#fff" }}>
          <thead>
            <tr>
              <th>Exercise</th>
              <th>Sets</th>
              <th>Reps</th>
              <th>kg</th>
              <th>Rest</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((ex) => (
              <tr key={ex.id}>
                <td>
                  {ex.name}
                  {ex.notes ? (
                    <div style={{ color: "#9ca3af", fontSize: "0.75rem" }}>{ex.notes}</div>
                  ) : null}
                </td>
                <td>{ex.target_sets}</td>
                <td>{ex.target_reps}</td>
                <td>{ex.target_weight_kg != null ? ex.target_weight_kg : "—"}</td>
                <td>{ex.rest_seconds}s</td>
                <td>
                  <Button
                    variant="link"
                    size="sm"
                    style={{ color: "#F87171" }}
                    onClick={() => handleDeleteExercise(ex.id)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    ));
  };

  if (loading) {
    return (
      <Container fluid className="text-center py-5">
        <Spinner animation="border" style={{ color: SIDEBAR_BLUE }} />
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="d-flex align-items-center mb-4">
        <FaDumbbell className="me-2" style={{ color: SIDEBAR_BLUE, fontSize: "1.5rem" }} />
        <h2 className="mb-0" style={{ color: "#fff" }}>Workout Planner</h2>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={4} className="mb-4">
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 style={{ color: "#fff", margin: 0 }}>Your plans</h5>
                <Button
                  size="sm"
                  onClick={openCreatePlanModal}
                  style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}
                >
                  <FaPlus className="me-1" /> New plan
                </Button>
              </div>

              {plans.length === 0 ? (
                <p style={{ color: "#9ca3af" }}>
                  No plans yet. Create a date range, then build each day separately.
                </p>
              ) : (
                <ListGroup variant="flush">
                  {plans.map((plan) => (
                    <ListGroup.Item
                      key={plan.id}
                      action
                      onClick={() => openPlan(plan.id)}
                      active={selectedPlan?.id === plan.id}
                      style={{
                        backgroundColor: selectedPlan?.id === plan.id ? "#1e3a5f" : "#2d2d2d",
                        border: `1px solid ${SIDEBAR_BLUE}`,
                        color: "#fff",
                        marginBottom: 8,
                        borderRadius: 4,
                      }}
                    >
                      <div className="d-flex justify-content-between">
                        <strong>{plan.name}</strong>
                        <Button
                          variant="link"
                          size="sm"
                          style={{ color: "#F87171" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePlan(plan.id);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                      <small style={{ color: "#9ca3af" }}>
                        {displayDate(plan.start_date)} → {displayDate(plan.end_date)}
                      </small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          {selectedPlan && scheduledDays.length > 0 && (
            <Card style={{ ...cardStyle, marginTop: 16 }}>
              <Card.Body>
                <h6 style={{ color: "#fff" }}>Days with workouts</h6>
                <ListGroup variant="flush">
                  {scheduledDays.map((day) => (
                    <ListGroup.Item
                      key={day.id}
                      action
                      onClick={() => setSelectedDay(parseDate(day.date))}
                      style={{
                        backgroundColor: day.date === selectedDayStr ? "#1e3a5f" : "#2d2d2d",
                        border: `1px solid ${SIDEBAR_BLUE}`,
                        color: "#fff",
                        marginBottom: 6,
                        borderRadius: 4,
                        fontSize: "0.9rem",
                      }}
                    >
                      {displayDate(day.date)}
                      <span style={{ color: "#9ca3af", marginLeft: 8 }}>
                        {(day.exercises || []).length} exercise(s)
                      </span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={8}>
          <Card style={cardStyle}>
            <Card.Body>
              {!selectedPlan ? (
                <p style={{ color: "#9ca3af" }}>Select a plan to build day-by-day workouts.</p>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                    <div>
                      <h4 style={{ color: "#fff" }}>{selectedPlan.name}</h4>
                      <p style={{ color: "#d1d5db", marginBottom: 0 }}>
                        <FaCalendarAlt className="me-2" />
                        {displayDate(selectedPlan.start_date)} → {displayDate(selectedPlan.end_date)}
                      </p>
                    </div>
                    <Button
                      variant="outline-info"
                      onClick={() => openEditPlanModal(selectedPlan)}
                    >
                      <FaEdit className="me-1" /> Edit plan
                    </Button>
                  </div>

                  <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => shiftSelectedDay(-1)}
                    >
                      <FaChevronLeft />
                    </Button>
                    <DatePicker
                      selected={selectedDay}
                      onChange={(date) => setSelectedDay(clampSelectedDay(date))}
                      dateFormat="dd/MM/yyyy"
                      minDate={parseDate(selectedPlan.start_date)}
                      maxDate={parseDate(selectedPlan.end_date)}
                      customInput={<Form.Control style={{ ...inputStyle, width: 140 }} />}
                    />
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => shiftSelectedDay(1)}
                    >
                      <FaChevronRight />
                    </Button>
                    {activeDayWorkout && (
                      <Button size="sm" variant="outline-danger" onClick={handleClearDay}>
                        Clear day
                      </Button>
                    )}
                    {activeDayWorkout && (activeDayWorkout.exercises || []).length > 0 && (
                      <Button
                        size="sm"
                        variant="outline-info"
                        disabled={savingExercise}
                        onClick={handleCopyDayToSelected}
                      >
                        Copy this day → selected weekdays
                      </Button>
                    )}
                  </div>

                  <div
                    className="mb-4"
                    style={{
                      backgroundColor: "#2d2d2d",
                      border: `1px solid ${SIDEBAR_BLUE}`,
                      borderRadius: 4,
                      padding: "12px 14px",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                      <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                        Apply new exercises to
                      </div>
                      <div style={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                        {applyDates.length} day{applyDates.length === 1 ? "" : "s"} in this plan
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      <Button size="sm" variant="outline-info" onClick={selectThisDayOnly}>
                        This day
                      </Button>
                      <Button size="sm" variant="outline-info" onClick={selectWeekdaysOnly}>
                        Mon–Fri
                      </Button>
                      <Button size="sm" variant="outline-info" onClick={selectAllWeekdays}>
                        All days
                      </Button>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {WEEKDAY_LABELS.map((day) => (
                        <Button
                          key={day.value}
                          size="sm"
                          variant={applyWeekdays.has(day.value) ? "primary" : "outline-secondary"}
                          style={
                            applyWeekdays.has(day.value)
                              ? { backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }
                              : undefined
                          }
                          onClick={() => toggleApplyWeekday(day.value)}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                    <p className="mb-0 mt-2" style={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                      Warm-ups and exercises you add will be created on every selected weekday in the plan range.
                    </p>
                  </div>

                  <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                    Previewing {displayDate(selectedDayStr)}
                    {activeDayWorkout ? "" : " — empty so far"}.
                  </p>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                      <h6 style={{ color: "#FBBF24", margin: 0 }}>Warm-up</h6>
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => openExerciseModal("warmup")}
                        disabled={savingExercise}
                      >
                        <FaPlus className="me-1" /> Custom
                      </Button>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {WARMUP_PRESETS.map((preset) => (
                        <Button
                          key={preset.name}
                          size="sm"
                          variant="outline-secondary"
                          disabled={savingExercise}
                          onClick={() => handleAddWarmupPreset(preset)}
                        >
                          + {preset.name}
                        </Button>
                      ))}
                    </div>
                    {renderExerciseTable(exercisesBySection("warmup"), true)}
                  </div>

                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 style={{ color: "#fff", margin: 0 }}>Exercises</h6>
                      <Button
                        size="sm"
                        onClick={() => openExerciseModal("main")}
                        style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}
                      >
                        <FaPlus className="me-1" /> Add exercise
                      </Button>
                    </div>
                    {renderExerciseTable(exercisesBySection("main"))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showPlanModal}
        onHide={() => {
          setShowPlanModal(false);
          resetPlanForm();
        }}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton closeVariant="white" style={{ borderColor: SIDEBAR_BLUE }}>
          <Modal.Title>{editingPlanId ? "Edit workout plan" : "New workout plan"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSavePlan}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={planForm.name}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. July strength block"
                style={inputStyle}
              />
            </Form.Group>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button size="sm" variant="outline-info" type="button" onClick={() => setRangePreset(7)}>7 days</Button>
              <Button size="sm" variant="outline-info" type="button" onClick={() => setRangePreset(15)}>15 days</Button>
              <Button size="sm" variant="outline-info" type="button" onClick={() => setRangePreset(30)}>30 days</Button>
            </div>
            {blockedDateIntervals.length > 0 && (
              <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                Dates already covered by other workout plans are disabled.
              </p>
            )}
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Start</Form.Label>
                  <DatePicker
                    selected={planForm.start_date}
                    onChange={(date) => setPlanForm((prev) => ({
                      ...prev,
                      start_date: date,
                      end_date: prev.end_date && date && prev.end_date < date ? date : prev.end_date,
                    }))}
                    dateFormat="dd/MM/yyyy"
                    excludeDateIntervals={blockedDateIntervals}
                    customInput={<Form.Control style={inputStyle} />}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>End</Form.Label>
                  <DatePicker
                    selected={planForm.end_date}
                    onChange={(date) => setPlanForm((prev) => ({ ...prev, end_date: date }))}
                    dateFormat="dd/MM/yyyy"
                    minDate={planForm.start_date}
                    excludeDateIntervals={blockedDateIntervals}
                    customInput={<Form.Control style={inputStyle} />}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={planForm.notes}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, notes: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderColor: SIDEBAR_BLUE }}>
            <Button variant="secondary" onClick={() => { setShowPlanModal(false); resetPlanForm(); }}>
              Cancel
            </Button>
            <Button type="submit" style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}>
              {editingPlanId ? "Save changes" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showExerciseModal}
        onHide={() => setShowExerciseModal(false)}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton closeVariant="white" style={{ borderColor: SIDEBAR_BLUE }}>
          <Modal.Title>
            {exerciseSection === "warmup" ? "Add warm-up" : "Add exercise"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddExercise}>
          <Modal.Body>
            <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
              Will be added to <strong style={{ color: "#fff" }}>{applyDates.length}</strong> day
              {applyDates.length === 1 ? "" : "s"} (based on weekday selection above).
            </p>
            {exerciseSection === "warmup" && (
              <div className="d-flex gap-2 mb-3">
                <Button
                  size="sm"
                  type="button"
                  variant={warmupMode === "duration" ? "warning" : "outline-secondary"}
                  onClick={() => setWarmupMode("duration")}
                >
                  Duration (walk / bike)
                </Button>
                <Button
                  size="sm"
                  type="button"
                  variant={warmupMode === "sets" ? "warning" : "outline-secondary"}
                  onClick={() => setWarmupMode("sets")}
                >
                  Sets / reps
                </Button>
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={exerciseForm.name}
                onChange={(e) => setExerciseForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={
                  exerciseSection === "warmup" && warmupMode === "duration"
                    ? "e.g. Walk 5'"
                    : "e.g. Bench press"
                }
                style={inputStyle}
              />
            </Form.Group>
            {!(exerciseSection === "warmup" && warmupMode === "duration") && (
              <Form.Group className="mb-3">
                <Form.Label>Muscle group</Form.Label>
                <Form.Select
                  value={exerciseForm.muscle_group}
                  onChange={(e) => setExerciseForm((prev) => ({ ...prev, muscle_group: e.target.value }))}
                  style={inputStyle}
                >
                  {MUSCLE_GROUPS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            {exerciseSection === "warmup" && warmupMode === "duration" ? (
              <Form.Group className="mb-3">
                <Form.Label>Duration (minutes)</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  required
                  value={exerciseForm.duration_minutes}
                  onChange={(e) => setExerciseForm((prev) => ({ ...prev, duration_minutes: e.target.value }))}
                  style={inputStyle}
                />
              </Form.Group>
            ) : (
              <>
                <Row>
                  <Col xs={4} className="mb-3">
                    <Form.Label>Sets</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      required
                      value={exerciseForm.target_sets}
                      onChange={(e) => setExerciseForm((prev) => ({ ...prev, target_sets: e.target.value }))}
                      style={inputStyle}
                    />
                  </Col>
                  <Col xs={4} className="mb-3">
                    <Form.Label>Reps</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      required
                      value={exerciseForm.target_reps}
                      onChange={(e) => setExerciseForm((prev) => ({ ...prev, target_reps: e.target.value }))}
                      style={inputStyle}
                    />
                  </Col>
                  <Col xs={4} className="mb-3">
                    <Form.Label>kg</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.5"
                      value={exerciseForm.target_weight_kg}
                      onChange={(e) => setExerciseForm((prev) => ({ ...prev, target_weight_kg: e.target.value }))}
                      placeholder="—"
                      style={inputStyle}
                    />
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Rest (seconds)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={exerciseForm.rest_seconds}
                    onChange={(e) => setExerciseForm((prev) => ({ ...prev, rest_seconds: e.target.value }))}
                    style={inputStyle}
                  />
                </Form.Group>
              </>
            )}
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                value={exerciseForm.notes}
                onChange={(e) => setExerciseForm((prev) => ({ ...prev, notes: e.target.value }))}
                style={inputStyle}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderColor: SIDEBAR_BLUE }}>
            <Button variant="secondary" onClick={() => setShowExerciseModal(false)}>Cancel</Button>
            <Button type="submit" disabled={savingExercise} style={{ backgroundColor: SIDEBAR_BLUE, borderColor: SIDEBAR_BLUE }}>
              {savingExercise ? "Adding…" : `Add to ${applyDates.length} day${applyDates.length === 1 ? "" : "s"}`}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default WorkoutPlanner;
